import { Injectable } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { MenuService as BaseMenuService } from 'app';

@Injectable({
  providedIn: 'root',
})
export class MenuService extends BaseMenuService<INavData> {
  
  constructor() {
      super();
  }

  protected createNavItems(lang: string, t: (key: string) => string): INavData[] {
    return [
      {
        name: t('dashboard'),
        url: '/'+lang+'/dashboard',
        iconComponent: { name: 'cil-speedometer' }
        // Visible to all users (no data-visible attribute)
      },
      {
        name: t('menu.info'),
        url: `/${lang}/media/info`,
        iconComponent: { name: 'info' }
      },
      {
        name: t('user_statistics'),
        url: '/'+lang+'/user-statistics',
        iconComponent: { name: 'cil-people' }
        // Visible to all users (no data-visible attribute)
      },
      {
        name: t('earnings_statistics'),
        url: '/'+lang+'/earnings-statistics',
        iconComponent: { name: 'cil-dollar' }
        // Visible to all users (no data-visible attribute)
      },
      {
        name: t('user_tree.title'),
        url: '/'+lang+'/user-tree',
        iconComponent: { name: 'cil-sitemap' }
        // Visible to all users (no data-visible attribute)
      },
      {
        name: t('order_products'),
        url: '/'+lang+'/order-wizard',
        iconComponent: { name: 'cil-cart' }
        // Visible to all users - no restrictions
      },
      {
        name: t('my_orders'),
        url: '/'+lang+'/my-orders',
        iconComponent: { name: 'cil-list' }
        // Visible to all users - no restrictions
      },
      {
        name: t('bookings_menu'),
        url: '/'+lang+'/bookings',
        iconComponent: { name: 'cil-notes' }
        // Visible to all users - for viewing all bookings (transactions and orders)
      },
      {
        name: t('my_payouts'),
        url: '/'+lang+'/my-payouts',
        iconComponent: { name: 'cil-money' }
        // Visible to all users - for requesting and viewing own payouts
      },
      {
        name: t('menu.media'),
        url: '/'+lang+'/media/media',
        iconComponent: { name: 'cil-image' }
        // Visible to all users (no data-visible attribute)
      },
      {
        name: t('menu.Community'),
        url: '/'+lang+'/community',
        iconComponent: { name: 'cil-chat-bubble' },
        attributes: {
          // Hidden only when the backend says community_enabled === false
          // (unconfirmed e-mail); absent flag = visible.
          'data-visible': 'flag:community_enabled',
        }
      },
      {
        name: t('admin_dashboard'),
        url: '/'+lang+'/admin/dashboard',
        iconComponent: { name: 'cil-shield-alt' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('users'),
        url: '/'+lang+'/admin/users',
        iconComponent: { name: 'cil-people' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('admin_user_tree.title'),
        url: '/'+lang+'/admin/user-tree',
        iconComponent: { name: 'cil-sitemap' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('orders'),
        url: '/'+lang+'/admin/orders',
        iconComponent: { name: 'cil-cart' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('user_pools_menu'),
        url: '/'+lang+'/admin/user-pools',
        iconComponent: { name: 'cil-wallet' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('card_accounts_menu'),
        url: '/'+lang+'/admin/card-accounts',
        iconComponent: { name: 'cil-credit-card' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('payouts'),
        url: '/'+lang+'/admin/payouts',
        iconComponent: { name: 'cil-money' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('admin_statistics'),
        url: '/'+lang+'/admin/statistics',
        iconComponent: { name: 'cil-graph' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',
        }
      },
      {
        name: t('ewallet_statistics'),
        url: '/'+lang+'/admin/ewallet-statistics',
        iconComponent: { name: 'cil-chart' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('blockchain_transactions'),
        url: '/'+lang+'/admin/blockchain-transactions',
        iconComponent: { name: 'cil-link' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',  // Only visible to admins
        }
      },
      {
        name: t('admin_kyc_menu'),
        url: '/'+lang+'/admin/kyc',
        iconComponent: { name: 'cil-shield-alt' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',
        }
      },
      {
        name: t('audit_trail'),
        url: '/'+lang+'/admin/audit-trail',
        iconComponent: { name: 'cil-history' },
        badge: {
          color: 'danger',
          text: 'SUPER'
        },
        attributes: {
          'data-visible': 'isSuperAdmin',  // Only visible to super admins
        }
      },
      {
        // Legal & Compliance Center audit. Admin-visible (not super-admin-only like
        // the PaperTrail audit trail) to match the backend, which gates
        // /api/admin/legal/* on ensure_admin!.
        //
        // Declared here in the shared credo menu so fac3less, distribution and
        // globalhealth all get it — they call super.createNavItems() and filter.
        // mnlo and nam3less override createNavItems outright, so it does not leak
        // into brands the gate is not enabled for.
        name: t('legal_acceptances'),
        url: '/'+lang+'/admin/legal-acceptances',
        iconComponent: { name: 'cil-check-circle' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',
        }
      },
      {
        // Community Network structure (roles, groups, channels, rights matrix).
        // Admin-visible here; area admins reach the same page through the
        // "Community admin" button inside the community shell (/community/admin).
        name: t('community.admin.menu'),
        url: '/'+lang+'/admin/community',
        iconComponent: { name: 'cil-people' },
        badge: {
          color: 'danger',
          text: 'ADMIN'
        },
        attributes: {
          'data-visible': 'isAdmin',
        }
      }
    ];
  }
}